import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);

  // Leggi il cookie nexallai_sso_session
  const getCookie = useCallback((name) => {
    const nameEQ = name + '=';
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i].trim();
      if (cookie.indexOf(nameEQ) === 0) {
        return cookie.substring(nameEQ.length, cookie.length);
      }
    }
    return null;
  }, []);

  // Decodifica JWT (se è un JWT)
  const decodeJWT = useCallback((token) => {
    try {
      const base64Url = token.split('.')[1];
      if (!base64Url) return null;
      
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      
      return JSON.parse(jsonPayload);
    } catch (err) {
      console.warn('⚠️ Impossibile decodificare JWT:', err);
      return null;
    }
  }, []);

  // Estrai email dal cookie o dalla call API
  const extractUserEmail = useCallback(async (sessionCookie) => {
    try {
      // TENTATIVO 1: Decodifica JWT
      const decoded = decodeJWT(sessionCookie);
      if (decoded && decoded.email) {
        console.log('📧 Email estratta da JWT:', decoded.email);
        return decoded.email;
      }
      
      // TENTATIVO 2: Chiama API user-info
      console.log('📡 Tentativo di recuperare email da API...');
      const response = await fetch('https://demo.nexallai.com/api/user', {
        method: 'GET',
        credentials: 'include', // Includi i cookie
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        if (userData.email) {
          console.log('📧 Email recuperata da API:', userData.email);
          return userData.email;
        }
      }
      
      console.warn('⚠️ Impossibile estrarre email - userò default');
      return null;
    } catch (err) {
      console.warn('⚠️ Errore nell\'estrazione email:', err);
      return null;
    }
  }, [decodeJWT]);

  // Controlla autenticazione al mount
  const checkAuthentication = useCallback(async () => {
    try {
      setIsLoading(true);
      const sessionCookie = getCookie('nexallai_sso_session');
      
      if (sessionCookie && sessionCookie.trim() !== '') {
        console.log('✅ Cookie di sessione trovato - Utente autenticato');
        
        // Estrai email dal token
        const email = await extractUserEmail(sessionCookie);
        
        setIsAuthenticated(true);
        setUser({ 
          authenticated: true,
          email: email || 'unknown@nexallai.com'
        });
      } else {
        console.log('❌ Cookie di sessione non trovato - Utente non autenticato');
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      console.error('Errore nel controllo autenticazione:', error);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, [getCookie, extractUserEmail]);

  // Controlla autenticazione al mount e quando la pagina diventa visible (ritorno dal login)
  useEffect(() => {
    checkAuthentication();

    // Ascolta quando la finestra ritorna visibile
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('📄 Pagina rivisualizzata - Ricontrollo autenticazione');
        checkAuthentication();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [checkAuthentication]);

  // Redirect a login
  const redirectToLogin = useCallback(() => {
    console.log('🔐 Reindirizzamento a pagina login...');
    window.location.href = 'https://demo.nexallai.com/oauth/consent';
  }, []);

  // Logout - cancella il cookie e reindirizza
  const logout = useCallback(() => {
    try {
      console.log('🚪 Logout in corso...');
      
      // Cancella il cookie impostando expiry nel passato
      document.cookie = 'nexallai_sso_session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.nexallai.com; SameSite=Lax';
      
      // Cancella eventuali cookie della sessione corrente anche per il dominio corrente
      document.cookie = 'nexallai_sso_session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      
      setIsAuthenticated(false);
      setUser(null);
      
      // Reindirizza alla homepage pubblica
      window.location.href = '/';
    } catch (error) {
      console.error('Errore durante logout:', error);
    }
  }, []);

  const value = {
    isAuthenticated,
    isLoading,
    user,
    redirectToLogin,
    logout,
    checkAuthentication
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve essere usato dentro AuthProvider');
  }
  return context;
};
